import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import TextArea from 'react-textarea-autosize';

import { Button, Input, Text } from '@components/common';
import { SlideOut, SlideOutCloseButton } from '@components/SlideOut';
import { ArrowRightSVG, CloseSVG, GlobeSVG } from '@components/svgs';
import { PrototypeUrl } from '@lib/prototype-url/PrototypeUrl';

import { MainAgentState } from '../agents/main/types';

import { AttachmentsList } from './AttachmentsList';

interface Props {
  state: MainAgentState;
  setState: (state: MainAgentState) => void;
  onSendMessage: (message: string) => void;
  onAddPrototype: () => void;
  isMobile: boolean;
}

export const LandingPage = ({ state, setState, onSendMessage, onAddPrototype, isMobile }: Props) => {
  const [slideOutIsOpen, setSlideOutIsOpen] = useState(false);
  const [prototypeUrl, setPrototypeUrl] = useState<string>('');
  const [isValidPrototypeUrl, setIsValidPrototypeUrl] = useState<boolean>(true);
  const [prototypeUrlErrorMsg, setPrototypeUrlErrorMsg] = useState<string>('');
  const prototypeUrlInputRef = useRef<HTMLInputElement>(null);
  const mainTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (slideOutIsOpen && prototypeUrlInputRef.current) {
      prototypeUrlInputRef.current.focus();
    }
  }, [slideOutIsOpen]);

  useEffect(() => {
    if (mainTextareaRef.current) {
      mainTextareaRef.current.focus();
    }
  }, []);

  const handleOnUrlChange = (value: string) => {
    const newUrl = new PrototypeUrl(value, 'figma');

    setIsValidPrototypeUrl(newUrl.isValid());
    setPrototypeUrlErrorMsg(newUrl.getErrorMessage());
    setPrototypeUrl(value);
  };

  const handleCloseSlideout = () => {
    setSlideOutIsOpen(false);
    if (mainTextareaRef.current) {
      mainTextareaRef.current.focus();
    }
  };

  const handleSubmitSlideout = () => {
    setState({
      ...state,
      attachments: [
        ...(state.attachments || []),
        {
          type: 'prototype_url',
          content: prototypeUrl
        }
      ]
    });
    setSlideOutIsOpen(false);
    if (mainTextareaRef.current) {
      mainTextareaRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isValidPrototypeUrl && prototypeUrl) {
        handleSubmitSlideout();
      }
    }
  };

  const researchGoal = state.study?.research_goal ?? '';
  const hasResearchGoal = researchGoal.trim().length > 0;

  return (
    <>
      <section className='px-page'>
        <main className='mx-auto my-7 max-w-3xl'>
          <h1 className='font-marketing text-center text-5xl font-bold' style={{ lineHeight: '56px' }}>
            What can we help you learn?
          </h1>
          <h2 className='mb-6 text-center text-xl font-medium text-gray-500'>
            We'll draft an unmoderated prototype test to share with users.
          </h2>

          <section className='p-4'>
            <TextArea
              ref={mainTextareaRef}
              className='focus:outline-none w-full resize-none border-gray-200 placeholder-gray-500 focus:border-none focus:shadow-none focus:ring-0'
              minRows={5}
              maxRows={5}
              placeholder="Enter what you're looking to learn..."
              value={researchGoal}
              onChange={(e) =>
                setState({
                  ...state,
                  study: {
                    ...state.study,
                    research_goal: e.target.value
                  }
                })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!e.shiftKey && hasResearchGoal) {
                    onSendMessage(researchGoal);
                  }
                }
              }}
            />

            <div className='mt-2 flex items-center justify-between'>
              <div className='flex items-center space-x-2'>
                <AttachmentsList attachments={state.attachments} />
                {(!state.attachments || state.attachments.length === 0) && (
                  <Button medium={isMobile} className='py-2' onClick={() => setSlideOutIsOpen(true)} weight='normal'>
                    <img className='mr-2 h-4 w-4' src='/temp-assets/figma.png' alt='Figma logo' />
                    Add prototype
                  </Button>
                )}
              </div>

              <Button
                medium={isMobile}
                aria-label='Submit form'
                primary
                disabled={!hasResearchGoal}
                onClick={() => onSendMessage(researchGoal)}
              >
                <ArrowRightSVG className='h-4 w-4' />
              </Button>
            </div>
          </section>
        </main>
      </section>

      <SlideOut
        className='flex flex-col'
        hasMask
        isOpen={slideOutIsOpen}
        onClose={handleCloseSlideout}
        width={isMobile ? '100%' : 560}
        hideCloseButton
      >
        <header className='flex items-center justify-between border-b border-gray-200 p-6'>
          <Text as='h3' h='700'>
            Add prototype
          </Text>

          <SlideOutCloseButton>
            <CloseSVG className='h-4 w-4' />
          </SlideOutCloseButton>
        </header>

        <section className='p-6'>
          <Text bold>Prototype link</Text>

          <Text className='my-3 text-sm' color='gray-500'>
            Insert your prototype link.
          </Text>

          <Input
            ref={prototypeUrlInputRef}
            type='url'
            placeholder='Enter Figma link...'
            onChange={handleOnUrlChange}
            onKeyDown={handleKeyDown}
            id='prototypeUrl'
            value={prototypeUrl}
            className='w-full'
            error={Boolean(prototypeUrlErrorMsg)}
            required
          />

          {prototypeUrlErrorMsg && <Text className='mt-2 text-xs text-red-500'>{prototypeUrlErrorMsg}</Text>}
        </section>

        <footer className='mt-auto border-t border-gray-200 px-4 py-6'>
          <Button primary disabled={!isValidPrototypeUrl || !prototypeUrl} onClick={handleSubmitSlideout}>
            Done
          </Button>
        </footer>
      </SlideOut>
    </>
  );
};
